import React, { FC, useState, useEffect, useRef } from 'react'
import { Button, Table, Input, Row, Col, Form } from 'antd'
import { YhBox } from '@/components/YhBox'
import { ColumnsType } from 'antd/es/table'
import { RouterParamType } from '@/model/navModel'
import { supplierService } from '../../../supplierService'
import { IFmsSupplierAccountShopDetail, IFmsSupplierAccountShopPageSearch, ICommonSearchFields, IFmsDictDetail } from '../../../supplierTypes'
import { ISearchPage, IPgProps } from '@/types/ScmTypes'
import { DEFAULT_PAGE_SIZE, DEFAULT_FIRST_PAGE_NUM } from '@/utils/constants'
import { TablePaginationConfig } from 'antd/lib/table'
import { transPgToPagination } from '@/model/globalModel'
import { useSupplierDetailsMode } from '../supplierDetailsModel'
import YhTootip from '@/components/YhTootip'
import { bigNumber, generateUniqueId } from '@/utils/utils'

export interface ISummaryData {
    id: string
    [key: string]: any;  
}

export const SupplierAccount: FC<RouterParamType> = ({ id, mode }) => {
    const { editFormVal } = useSupplierDetailsMode()
    // 查询条件
    const [searchParam, setSearchParam] = useState<ICommonSearchFields>({})

    // 数据源
    const [dataSource, setDataSource] = useState<IFmsSupplierAccountShopDetail[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    const accountTypeListRef = useRef<IFmsDictDetail[]>([])
    const [summaryColumns, setSummaryColumns] = useState<ColumnsType<any>>([])
    const [summaryDataSource, setSummaryDataSource] = useState<ISummaryData[]>([])

    useEffect(() => {
        if (editFormVal.code) {
            // getAccountType()
            loadData()
        }
    }, [editFormVal.code, searchParam])

    // 表格列定义
    const columns: ColumnsType<IFmsSupplierAccountShopDetail> = [
        {
            title: '序号',
            width: 60,
            fixed: 'left',
            align: 'center',
            render: (t: any, r: any, i: number) => i + 1,
        },
        {
            title: '开户门店',
            width: 120,
            dataIndex: 'shopName',
            render: (text: string) => <YhTootip text={text} />,
        },
        {
            title: '账户类型',
            width: 120,
            dataIndex: 'accountTypeName',
            render: (text: string) => <YhTootip text={text} />,
        },
        {
            title: '开户时间',
            width: 120,
            dataIndex: 'createTime',
            render: (text: string) => <YhTootip text={text} />,
        },
        {
            title: '账户额度',
            width: 100,
            dataIndex: 'creditAmount',
            render: (value: number) => value?.toFixed(2),
        },
        {
            title: '已用额度',
            width: 120,
            dataIndex: 'usedAmount',
            render: (value: number) => value?.toFixed(2),
        },
        {
            title: '可用额度',
            width: 120,
            dataIndex: 'availableAmount',
            render: (value: number) => value?.toFixed(2),
        },
        {
            title: '剩余金额',
            width: 120,
            dataIndex: 'remainingAmount',
            render: (value: number) => value?.toFixed(2),
        },
    ]

    // const summaryColumns: ColumnsType<any> = [
    //     {
    //         title: '授信客户',
    //         align: 'center',
    //         children: [
    //             {
    //                 title: '授信额度',
    //                 dataIndex: 'creditCount',
    //                 width: 100,
    //                 align: 'center',
    //             },
    //             {
    //                 title: '剩余总额',
    //                 dataIndex: 'creditAmount',
    //                 width: 120,
    //                 align: 'center',
    //                 render: (value: number) => value?.toFixed(2) || '0.00',
    //             },
    //         ],
    //     },
    //     {
    //         title: '现金账户',
    //         align: 'center',
    //         children: [
    //             {
    //                 title: '现金总金额',
    //                 dataIndex: 'cashAmount',
    //                 width: 120,
    //                 align: 'center',
    //                 render: (value: number) => value?.toFixed(2) || '0.00',
    //             },
    //         ],
    //     },
    //     {
    //         title: '返利账户',
    //         align: 'center',
    //         children: [
    //             {
    //                 title: '返利金额',
    //                 dataIndex: 'profitAmount',
    //                 width: 120,
    //                 align: 'center',
    //                 render: (value: number) => value?.toFixed(2) || '0.00',
    //             },
    //         ],
    //     },
    //     {
    //         title: '月结账户',
    //         align: 'center',
    //         children: [
    //             {
    //                 title: '账户余额',
    //                 dataIndex: 'monthlyAmount',
    //                 width: 120,
    //                 align: 'center',
    //                 render: (value: number) => value?.toFixed(2) || '0.00',
    //             },
    //         ],
    //     },
    // ]

    // 加载数据
    const loadData = async () => {
        setLoading(true)
        try {
            const searchParams = {
                ...searchParam,
                partnerTypeCode: 'user-type.supplier',
                partnerCode: editFormVal.code as string,
                shopCode: '',
                sourceApp: 'fms',
            }

            const { retData } = await supplierService.fmsSupplierAccountShopPage(searchParams)

            // summaryDataSource
            setDataSource(retData)
            initSummaryTable(retData)
        } catch (error) {
            setDataSource([])
        } finally {
            setLoading(false)
        }
    }

    // const getAccountType = async () => {
    //     const searchParams = {
    //         pcode: 'account-type',
    //         sourceApp: 'fms',
    //     }
    //     const { retData } = await supplierService.fmsDictFind(searchParams)
    //     accountTypeListRef.current = retData.filter((i) => i.enable === 1)
    // }

    const initSummaryTable = (list: IFmsSupplierAccountShopDetail[]) => {
        // 根据value相同分类然后组装成一个新的数组
        const groupedByValue = list.reduce((acc, item) => {
            const value = item.accountTypeCode as string
            if (!acc[value]) {
                acc[value] = []
            }
            acc[value].push(item)
            return acc
        }, {} as Record<string, IFmsDictDetail[]>)

        let columns: ColumnsType<any> = [],
            data: ISummaryData[] = [{
                id:generateUniqueId()
            }]
        // 将分组结果转换为数组格式
        const groupedArray = Object.entries(groupedByValue).forEach(([value, items]) => {
            // value,
            // name: items[0].accountTypeName,
            // items,
            // count: items.length,

            // columns
            const str = value.replace(".","-")
            const creditAmountDataIndex = `${str}-creditAmount`
            const remainingAmountDataIndex = `${str}-remainingAmount`
            columns.push({
                title: items[0].accountTypeName,
                align: 'center',
                children: [
                    {
                        title: '授信额度',
                        dataIndex: creditAmountDataIndex,
                        width: 100,
                        align: 'center',
                        render: (value: number) => value?.toFixed(2) || '0.00',
                    },
                    {
                        title: '剩余总额',
                        dataIndex: remainingAmountDataIndex,
                        width: 120,
                        align: 'center',
                        render: (value: number) => value?.toFixed(2) || '0.00',
                    },
                ],
            })

            //list
            data[0][creditAmountDataIndex] = 0
            data[0][remainingAmountDataIndex] = 0

            items.forEach(i => {
                 data[0][creditAmountDataIndex] = bigNumber.add(data[0][creditAmountDataIndex],i.creditAmount)
                 data[0][remainingAmountDataIndex] =  bigNumber.add(data[0][remainingAmountDataIndex],i.remainingAmount)
            })

        })

        setSummaryColumns(columns)
        setSummaryDataSource(data)

        console.log(groupedByValue)
        console.log('按value分类的结果:', groupedArray)
    }

    return (
        <YhBox style={{ padding: '20px' }}>
            {/* 查询条件 */}
            {/* <Row gutter={10}>
                <Col span={6}>
                    <Input
                        style={{ flex: 1, marginRight: '10px' }}
                        placeholder="供应商编码"
                        value={searchParam.materialCode}
                        onChange={(e) => {
                            setSearchParam({
                                ...searchParam,
                                materialCode: e.target.value,
                            })
                        }}
                    />
                </Col>
            </Row> */}

            {/* 数据表格 */}
            <Table
                bordered
                rowKey={(record) => record.id}
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                scroll={{ x: 'max-content', y: '400px' }}
                pagination={false}
            />

            <div
                style={{
                    margin: '20px 0',
                    color: '#434343',
                    fontWeight: 'bold',
                }}
            >
                账户汇总
            </div>

            <Table
                bordered
                loading={loading}
                rowKey={(record) => record.id}
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={summaryColumns}
                dataSource={summaryDataSource}
            />
        </YhBox>
    )
}
